import React, { useState, useEffect, useMemo, Suspense, lazy, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import useWebSocket from './hooks/useWebSocket';
import Header from './components/Header';
import UserDashboard from './components/UserDashboard';
import Footer from './components/Footer';
import HomePage from './components/HomePage';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import { Capacitor } from '@capacitor/core';
import { StatusBar as CapacitorStatusBar, Style } from '@capacitor/status-bar';
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { initializePushNotifications, registerPushToken, removeNotificationListeners } from './services/pushNotifications';
import './styles/index.css';
import './styles/native.css';
import './styles/native-app.css';
import { info, error as logError } from './utils/logger';
import { formatDate, formatTime } from './utils/dateUtils';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Lazy load heavy components for better performance
const CreateCard = lazy(() => import('./pages/CreateCard'));
const CreateCardV2 = lazy(() => import('./pages/CreateCardV2'));
const ProcessingPage = lazy(() => import('./pages/ProcessingPage'));
const MyCards = lazy(() => import('./pages/MyCards'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PricingTest = lazy(() => import('./pages/PricingTest'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const UserArea = lazy(() => import('./pages/UserArea'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
// eslint-disable-next-line no-unused-vars
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const AchievementsPanel = lazy(() => import('./pages/Achievements/AchievementsPanel'));
const PublicProfile = lazy(() => import('./pages/PublicProfile/PublicProfile'));
const CreditHistory = lazy(() => import('./pages/Settings/CreditHistory'));

// Theme Hook - Always Dark Mode
function useTheme() {
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    // Always use dark mode
    setDarkMode(true);
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  const toggleTheme = () => {
    // Theme toggle disabled - always dark mode
    return;
  };

  return [darkMode, toggleTheme];
}


// HomePage component is now imported from ./components/HomePage

// Notification Component
function NotificationCenter({ notifications, onRemove, onClear }) {
  if (notifications.length === 0) return null;

  return (
    <div className="notification-center">
      <div className="notification-header">
        <h4>Notifications</h4>
        {notifications.length > 1 && (
          <button onClick={onClear} className="clear-all-btn">
            Clear All
          </button>
        )}
      </div>
      <div className="notifications-list">
        {notifications.map(notification => (
          <div 
            key={notification.id} 
            className={`notification ${notification.type || 'info'}`}
          >
            <div className="notification-content">
              <h5>{notification.title}</h5>
              <p>{notification.message}</p>
              <span className="notification-time">
                {formatTime(notification.timestamp)}
              </span>
            </div>
            <button 
              onClick={() => onRemove(notification.id)}
              className="notification-close"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Real-time Status Bar
function StatusBar({ wsConnected, activeUsers, adminMetrics }) {
  return (
    <div className="status-bar">
      <div className="status-item">
        <span className={`status-dot ${wsConnected ? 'connected' : 'disconnected'}`}></span>
        <span className="status-text">
          {wsConnected ? 'Live' : 'Offline'}
        </span>
      </div>
      {activeUsers > 0 && (
        <div className="status-item">
          <span className="status-text">{activeUsers} users online</span>
        </div>
      )}
      {adminMetrics && (
        <div className="status-item">
          <span className="status-text">
            {adminMetrics.todayGenerations} cards today
          </span>
        </div>
      )}
    </div>
  );
}



// Removed inline admin dashboard - now using separate AdminDashboard.js component
// eslint-disable-next-line no-unused-vars  
function RemovedInlineDashboard({ user, adminMetrics, wsConnected, setCurrentPage, handleViewUserDetails }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  // const [selectedUser, setSelectedUser] = useState(null);
  const [editingCredits, setEditingCredits] = useState(null);
  const [newCredits, setNewCredits] = useState('');

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchDashboardData();
      fetchUsers();
      fetchAnalytics();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin?action=dashboard-stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDashboardData(response.data);
      }
    } catch (err) {
      logError('Failed to fetch dashboard data', { error: err.message, stack: err.stack });
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin?action=users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (err) {
      logError('Failed to fetch users', { error: err.message, stack: err.stack });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin?action=analytics', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAnalytics(response.data.analytics);
      }
    } catch (err) {
      logError('Failed to fetch analytics', { error: err.message, stack: err.stack });
    }
  };

  const handleUpdateCredits = async (userId, credits) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/admin?action=update-credits', {
        userId,
        credits: parseInt(credits)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUsers(users.map(u => 
          u._id === userId ? { ...u, credits: parseInt(credits) } : u
        ));
        setEditingCredits(null);
        setNewCredits('');
        alert('Credits updated successfully!');
      }
    } catch (err) {
      logError('Failed to update credits', { error: err.message, stack: err.stack });
      alert('Failed to update credits');
    }
  };

  const handleUpdateRole = async (userId, role) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/admin?action=update-role', {
        userId,
        role
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUsers(users.map(u => 
          u._id === userId ? { ...u, role } : u
        ));
        alert('User role updated successfully!');
      }
    } catch (err) {
      logError('Failed to update role', { error: err.message, stack: err.stack });
      alert('Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId, userName, userEmail) => {
    // Double confirmation for user deletion
    const confirmMessage = `⚠️ DANGER: Delete User Account?\n\nUser: ${userName}\nEmail: ${userEmail}\n\nThis will permanently delete:\n• User account\n• All their card generations\n• All associated data\n\nThis action CANNOT be undone!\n\nType "DELETE" to confirm:`;
    
    const confirmation = prompt(confirmMessage);
    
    if (confirmation !== 'DELETE') {
      alert('User deletion cancelled. You must type "DELETE" exactly to confirm.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete('/api/admin?action=delete-user', {
        headers: { Authorization: `Bearer ${token}` },
        data: { userId }
      });
      
      if (response.data.success) {
        // Remove user from the list
        setUsers(users.filter(u => u._id !== userId));
        alert(`✅ User "${userName}" and ${response.data.deletedGenerations} card generations deleted successfully.`);
      }
    } catch (err) {
      logError('Failed to delete user', { error: err.message, stack: err.stack });
      const errorMessage = err.response?.data?.error || 'Failed to delete user';
      alert(`❌ Error: ${errorMessage}`);
    }
  };


  if (!user) return <div>Loading...</div>;

  if (user.role !== 'admin') {
    return <UserDashboard user={user} setCurrentPage={setCurrentPage} />;
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="container">
          <div className="admin-loading-container">
            <h3 style={{ 
              color: 'var(--color-text-primary)', 
              marginBottom: '1rem', 
              fontSize: '1.5rem',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              Loading Dashboard
            </h3>
            <p style={{ 
              color: 'var(--color-text-secondary)', 
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              Preparing your admin interface...
            </p>
            <div className="loading-bar-container">
              <div className="loading-bar"></div>
            </div>
            <div className="loading-text">
              Please wait while we load your data
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-container">
        {/* Admin Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Administration</h1>
            <p>System management and analytics</p>
          </div>
          <div className="admin-user">
            <div className="user-avatar">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <div className="user-name">{user.name}</div>
              <div className="user-role">Administrator</div>
            </div>
          </div>
        </div>

        <div className="admin-nav">
          <button 
            className={`nav-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button 
            className={`nav-btn ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            Analytics
          </button>
        </div>

        {activeTab === 'overview' && dashboardData && (
          <div className="admin-content">
            {/* Key Metrics Section */}
            <div className="metrics-section">
              <h2 className="section-title">Platform Overview</h2>
              <div className="metrics-grid">
                <div className="metric-card users-metric">
                  <div className="metric-header">
                    <div className="metric-icon users-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                    <div className="metric-trend positive">
                      <span>+{Math.round((dashboardData.stats.activeUsers / dashboardData.stats.totalUsers) * 100)}%</span>
                    </div>
                  </div>
                  <div className="metric-content">
                    <div className="metric-number">{dashboardData.stats.totalUsers}</div>
                    <div className="metric-label">Total Users</div>
                    <div className="metric-sublabel">{dashboardData.stats.activeUsers} active this month</div>
                  </div>
                </div>

                <div className="metric-card generations-metric">
                  <div className="metric-header">
                    <div className="metric-icon generations-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <div className="metric-trend positive">
                      <span>+12%</span>
                    </div>
                  </div>
                  <div className="metric-content">
                    <div className="metric-number">{dashboardData.stats.totalGenerations}</div>
                    <div className="metric-label">Cards Generated</div>
                    <div className="metric-sublabel">Total AI creations</div>
                  </div>
                </div>

                <div className="metric-card credits-metric">
                  <div className="metric-header">
                    <div className="metric-icon credits-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.9 1 3 1.9 3 3V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V9ZM12 8C14.8 8 17 10.2 17 13S14.8 18 12 18 7 15.8 7 13 9.2 8 12 8Z"/>
                      </svg>
                    </div>
                    <div className="metric-trend neutral">
                      <span>Stable</span>
                    </div>
                  </div>
                  <div className="metric-content">
                    <div className="metric-number">{dashboardData.stats.totalCreditsIssued.toLocaleString()}</div>
                    <div className="metric-label">Credits Distributed</div>
                    <div className="metric-sublabel">Platform economy</div>
                  </div>
                </div>

                <div className="metric-card system-metric">
                  <div className="metric-header">
                    <div className="metric-icon system-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <div className={`metric-trend ${dashboardData.stats.systemHealth === 'Operational' ? 'positive' : 'warning'}`}>
                      <span>{dashboardData.stats.dbResponseTime}</span>
                    </div>
                  </div>
                  <div className="metric-content">
                    <div className="metric-status">{dashboardData.stats.systemHealth}</div>
                    <div className="metric-label">System Status</div>
                    <div className="metric-sublabel">All services running</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity Section */}
            <div className="activity-section">
              <div className="section-header">
                <h2 className="section-title">Recent Activity</h2>
                <button className="view-all-btn">View All</button>
              </div>
              
              <div className="activity-grid">
                <div className="activity-panel">
                  <h3 className="panel-title">Latest Users</h3>
                  <div className="activity-list">
                    {dashboardData.recentActivity.recentUsers.slice(0, 5).map(user => (
                      <div key={user._id} className="activity-row">
                        <div className="user-avatar-small">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="user-info">
                          <div className="user-name">{user.name}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                        <div className="join-date">
                          {formatDate(user.createdAt)}
                        </div>
              </div>
            ))}
          </div>
        </div>

                <div className="activity-panel">
                  <h3 className="panel-title">Recent Generations</h3>
                  <div className="activity-list">
                    {dashboardData.recentActivity.recentGenerations.length > 0 ? (
                      dashboardData.recentActivity.recentGenerations.slice(0, 5).map(generation => (
                        <div key={generation._id} className="activity-row">
                          <div className="generation-icon">🎨</div>
                          <div className="generation-info">
                            <div className="generation-type">Card Generated</div>
                            <div className="generation-meta">User creation</div>
                          </div>
                          <div className="generation-date">
                            {formatDate(generation.createdAt)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state">
                        <div className="empty-icon">🎨</div>
                        <div className="empty-text">No recent generations</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions-section">
              <h2 className="section-title">Quick Actions</h2>
              <div className="actions-grid">
                <button className="action-card" onClick={() => setActiveTab('users')}>
                  <div className="action-icon">👥</div>
                  <div className="action-title">Manage Users</div>
                  <div className="action-description">Edit user accounts and permissions</div>
                </button>
                
                <button className="action-card" onClick={() => setActiveTab('analytics')}>
                  <div className="action-icon">📊</div>
                  <div className="action-title">View Analytics</div>
                  <div className="action-description">Platform usage and growth metrics</div>
                </button>
                
                <button className="action-card">
                  <div className="action-icon">⚙️</div>
                  <div className="action-title">System Settings</div>
                  <div className="action-description">Configure platform settings</div>
                </button>
                
                <button className="action-card">
                  <div className="action-icon">🔄</div>
                  <div className="action-title">Backup Data</div>
                  <div className="action-description">Create system backup</div>
                </button>
              </div>
            </div>
              </div>
            )}

        {activeTab === 'users' && (
          <div className="admin-users">
            <div className="users-header">
              <h2>User Management</h2>
              <div className="users-count">{users.length} total users</div>
            </div>
            
            <div className="users-table">
              <div className="table-header">
                <div className="th">User</div>
                <div className="th">Email</div>
                <div className="th">Credits</div>
                <div className="th">Role</div>
                <div className="th">Generations</div>
                <div className="th">Status</div>
                <div className="th">Actions</div>
          </div>

              {users.map(user => (
                <div key={user._id} className="table-row">
                  <div className="td">
                    <div className="user-cell">
                      <div className="user-avatar">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <div className="user-name">{user.name}</div>
                        <div className="user-joined">
                          Joined {formatDate(user.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="td">{user.email}</div>
                  <div className="td">
                    {editingCredits === user._id ? (
                      <div className="credit-edit">
              <input
                          type="number"
                          value={newCredits}
                          onChange={(e) => setNewCredits(e.target.value)}
                          className="credit-input"
                          placeholder={user.credits}
                        />
                        <button 
                          onClick={() => handleUpdateCredits(user._id, newCredits)}
                          className="btn-save"
                        >
                          ✓
                        </button>
                        <button 
                          onClick={() => {
                            setEditingCredits(null);
                            setNewCredits('');
                          }}
                          className="btn-cancel"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="credit-display">
                        <span className="credit-amount">{user.credits}</span>
                        <button 
                          onClick={() => {
                            setEditingCredits(user._id);
                            setNewCredits(user.credits);
                          }}
                          className="btn-edit"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="td">
                    <select
                      value={user.role || 'user'}
                      onChange={(e) => handleUpdateRole(user._id, e.target.value)}
                      className="role-select"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="td">
                    <span className="generation-count">{user.generationCount || 0}</span>
                  </div>
                  <div className="td">
                    <span className={`status-badge ${user.status}`}>
                      {user.status}
                    </span>
                  </div>
                  <div className="td">
                    <div className="action-buttons">
                      <button 
                        onClick={() => handleViewUserDetails(user._id)}
                        className="btn-view"
                        title="View user details"
                      >
                        👁️ View
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user._id, user.name, user.email)}
                        className="btn-delete"
                        title="Delete user permanently"
                        style={{
                          backgroundColor: '#ff4444',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginLeft: '8px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#cc0000';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#ff4444';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && analytics && (
          <div className="admin-analytics">
            <div className="analytics-header">
              <h2>Analytics & Insights</h2>
              <div className="analytics-summary">
                <div className="summary-stat">
                  <span className="stat-number">{analytics.topUsers?.length || 0}</span>
                  <span className="stat-label">Active Users</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-number">
                    {analytics.generationActivity?.reduce((sum, day) => sum + day.count, 0) || 0}
                  </span>
                  <span className="stat-label">Total Generations</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-number">
                    {analytics.userGrowth?.reduce((sum, day) => sum + day.count, 0) || 0}
                  </span>
                  <span className="stat-label">New Users (30d)</span>
                </div>
              </div>
            </div>

            <div className="analytics-grid">
              {/* Enhanced Top Users */}
              <div className="analytics-card full-width">
                <div className="card-header">
                  <h3>🏆 Top Creators</h3>
                  <span className="card-subtitle">Most active card generators</span>
                </div>
                <div className="top-users-enhanced">
                  {analytics.topUsers && analytics.topUsers.length > 0 ? (
                    analytics.topUsers.slice(0, 5).map((user, index) => {
                      const maxCount = analytics.topUsers[0]?.generationCount || 1;
                      const percentage = ((user.generationCount || 0) / maxCount) * 100;
                      
                      return (
                        <div key={user._id || index} className="top-user-enhanced">
                          <div className="user-rank">
                            <span className={`rank-badge rank-${index + 1}`}>#{index + 1}</span>
                          </div>
                          <div className="user-info">
                            <div className="user-avatar">
                              {(user.userName || user.userEmail || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div className="user-details">
                              <div className="user-name">{user.userName || 'Unknown User'}</div>
                              <div className="user-email">{user.userEmail || 'No email'}</div>
                            </div>
                          </div>
                          <div className="user-stats">
                            <div className="stat-bar">
                              <div 
                                className="stat-fill" 
                                style={{width: `${percentage}%`}}
                              ></div>
                            </div>
                            <span className="stat-count">{user.generationCount || 0} cards</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">📊</div>
                      <p>No user activity data available yet</p>
                      <span>Users will appear here as they create cards</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Enhanced User Growth Chart */}
              <div className="analytics-card">
                <div className="card-header">
                  <h3>📈 User Growth</h3>
                  <span className="card-subtitle">New registrations over time</span>
                </div>
                <div className="chart-container">
                  {analytics.userGrowth && analytics.userGrowth.length > 0 ? (
                    <div className="enhanced-chart">
                      <div className="chart-area">
                        {analytics.userGrowth.map((day, index) => {
                          const maxCount = Math.max(...analytics.userGrowth.map(d => d.count));
                          const height = maxCount > 0 ? (day.count / maxCount) * 100 : 10;
                          
                          return (
                            <div key={day._id} className="chart-column">
                              <div className="chart-tooltip">
                                <span>{day.count} users</span>
                                <span>{day._id}</span>
                              </div>
                              <div 
                                className="chart-bar-enhanced" 
                                style={{height: `${Math.max(height, 5)}%`}}
                              >
                                <span className="bar-value">{day.count}</span>
                              </div>
                              <div className="chart-label">{day._id.slice(-2)}</div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="chart-summary">
                        <span>Total: {analytics.userGrowth.reduce((sum, day) => sum + day.count, 0)} new users</span>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-chart">
                      <div className="empty-icon">📈</div>
                      <p>No growth data available</p>
                      <span>Chart will populate as users join</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Enhanced Generation Activity */}
              <div className="analytics-card">
                <div className="card-header">
                  <h3>🎨 Generation Activity</h3>
                  <span className="card-subtitle">Daily card creation volume</span>
                </div>
                <div className="activity-enhanced">
                  {analytics.generationActivity && analytics.generationActivity.length > 0 ? (
                    <>
                      <div className="activity-chart-enhanced">
                        {analytics.generationActivity.slice(0, 7).map((day, index) => {
                          const maxCount = Math.max(...analytics.generationActivity.map(d => d.count));
                          const percentage = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                          
                          return (
                            <div key={day._id} className="activity-day">
                              <div className="day-header">
                                <span className="day-date">{day._id}</span>
                                <span className="day-count">{day.count}</span>
                              </div>
                              <div className="day-bar">
                                <div 
                                  className="day-fill" 
                                  style={{width: `${Math.max(percentage, 5)}%`}}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="activity-summary">
                        <div className="summary-item">
                          <span className="summary-label">Peak Day:</span>
                          <span className="summary-value">
                            {analytics.generationActivity.reduce((max, day) => 
                              day.count > max.count ? day : max, {count: 0, _id: 'None'}
                            )._id}
                          </span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Average:</span>
                          <span className="summary-value">
                            {Math.round(analytics.generationActivity.reduce((sum, day) => sum + day.count, 0) / analytics.generationActivity.length)} per day
                          </span>
                        </div>
                </div>
              </>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">🎨</div>
                      <p>No generation activity yet</p>
                      <span>Activity will show as users create cards</span>
                    </div>
            )}
          </div>
              </div>

              {/* New Card Type Popularity */}
              <div className="analytics-card">
                <div className="card-header">
                  <h3>🃏 Card Type Popularity</h3>
                  <span className="card-subtitle">Most popular card styles</span>
                </div>
                <div className="card-types-chart">
                  {[
                    {type: 'Trading Cards', count: 45, color: '#FFD700'},
                    {type: 'Football', count: 28, color: '#32CD32'},
                    {type: 'Baseball', count: 15, color: '#FF6347'},
                    {type: 'Basketball', count: 12, color: '#4169E1'}
                  ].map((cardType, index) => {
                    const maxCount = 45;
                    const percentage = (cardType.count / maxCount) * 100;
                    
                    return (
                      <div key={cardType.type} className="card-type-item">
                        <div className="type-info">
                          <span className="type-name">{cardType.type}</span>
                          <span className="type-count">{cardType.count} cards</span>
                        </div>
                        <div className="type-bar">
                          <div 
                            className="type-fill" 
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: cardType.color
                            }}
                          ></div>
                        </div>
                        <span className="type-percentage">{Math.round(percentage)}%</span>
                      </div>
                    );
                  })}
                </div>
        </div>

              {/* Performance Metrics */}
              <div className="analytics-card">
                <div className="card-header">
                  <h3>⚡ Performance Metrics</h3>
                  <span className="card-subtitle">System health & speed</span>
                </div>
                <div className="performance-metrics">
                  <div className="metric-item">
                    <div className="metric-icon">🚀</div>
                    <div className="metric-details">
                      <span className="metric-label">Avg. Generation Time</span>
                      <span className="metric-value">18.5s</span>
                    </div>
                    <div className="metric-status good">Good</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-icon">✅</div>
                    <div className="metric-details">
                      <span className="metric-label">Success Rate</span>
                      <span className="metric-value">94.2%</span>
                    </div>
                    <div className="metric-status excellent">Excellent</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-icon">💾</div>
                    <div className="metric-details">
                      <span className="metric-label">Database Response</span>
                      <span className="metric-value">125ms</span>
                    </div>
                    <div className="metric-status good">Good</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-icon">🌐</div>
                    <div className="metric-details">
                      <span className="metric-label">API Uptime</span>
                      <span className="metric-value">99.8%</span>
                    </div>
                    <div className="metric-status excellent">Excellent</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// AppContent component that uses React Router hooks
function AppContent() {
  const { user, loading: authLoading, logout: authLogout } = useAuth();
  const isLoggedIn = !!user;
  const location = useLocation();
  const navigate = useNavigate();
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);
  
  // Get current page from React Router location
  const currentPage = location.pathname === '/' ? 'home' : location.pathname.slice(1);
  
  // Navigation function using React Router
  const navigateToPage = useCallback((page) => {
    const path = page === 'home' ? '/' : `/${page}`;
    navigate(path);
  }, [navigate]);
  
  const [darkMode, toggleTheme] = useTheme();
  
  // Extract URL parameters for verification
  const verificationToken = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  }, []);
  
  // Initialize WebSocket and analytics
  const {
    connected: wsConnected,
    activeUsers,
    adminMetrics,
    notifications,
    trackPageView,
    notifyGenerationStarted,
    removeNotification,
    clearNotifications
  } = useWebSocket(user, isLoggedIn);

  // Track page views
  useEffect(() => {
    if (isLoggedIn) {
      trackPageView(currentPage);
    }
  }, [currentPage, isLoggedIn, trackPageView]);

  // Authentication is now handled by AuthContext
  
  // Check if we're on the verify-email page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/verify-email' || urlParams.get('token')) {
      navigateToPage('verify-email');
    }
  }, [navigateToPage]);

  // Add native platform class to body for CSS targeting
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('native-platform');
      if (Capacitor.getPlatform() === 'ios') {
        document.body.classList.add('ios-platform');
      } else if (Capacitor.getPlatform() === 'android') {
        document.body.classList.add('android-platform');
      }
    }
  }, []);

  // Initialize Capacitor features (mobile only)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Initialize Status Bar
    const initStatusBar = async () => {
      try {
        await CapacitorStatusBar.setStyle({ style: Style.Dark });
        await CapacitorStatusBar.setBackgroundColor({ color: '#000000' });
        await CapacitorStatusBar.setOverlaysWebView({ overlay: true });
      } catch (err) {
        logError('Error setting status bar', { error: err.message, stack: err.stack });
      }
    };

    // Hide splash screen when app is ready
    const hideSplash = async () => {
      try {
        await SplashScreen.hide();
      } catch (err) {
        logError('Error hiding splash screen', { error: err.message, stack: err.stack });
      }
    };

    // Initialize Push Notifications
    const initPushNotifications = async () => {
      try {
        const result = await initializePushNotifications();
        if (result.success) {
          info('Push notifications initialized');
        }
      } catch (err) {
        logError('Error initializing push notifications', { error: err.message, stack: err.stack });
      }
    };

    // Initialize App lifecycle handlers
    const initAppLifecycle = () => {
      // Handle app state changes
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        info('App state changed', { isActive });
      });

      // Handle back button (Android)
      CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          CapacitorApp.exitApp();
        } else {
          window.history.back();
        }
      });
    };

    initStatusBar();
    initPushNotifications();
    initAppLifecycle();
    
    // Hide splash screen after a short delay
    setTimeout(() => {
      hideSplash();
    }, 2000);

    // Cleanup
    return () => {
      removeNotificationListeners();
    };
  }, []);

  // Register push token after user login
  useEffect(() => {
    if (Capacitor.isNativePlatform() && isLoggedIn && user?.userId) {
      registerPushToken(user.userId).catch(err => {
        logError('Error registering push token', { error: err.message, stack: err.stack });
      });
    }
  }, [isLoggedIn, user]);

  // User profile is now managed by AuthContext



  const handleLogout = () => {
    authLogout();
    navigateToPage('home');
  };

  const handleViewUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/admin?action=user-details&userId=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const userDetails = response.data.user;
        const generations = response.data.generations;
        
        // Create a detailed modal or alert with user info
        const detailsText = `
User Details:
Name: ${userDetails.name}
Email: ${userDetails.email}
Credits: ${userDetails.credits}
Role: ${userDetails.role}
Joined: ${formatDate(userDetails.createdAt)}
Total Cards: ${generations.length}
Last Active: ${userDetails.lastActive ? formatDate(userDetails.lastActive) : 'Unknown'}
        `;
        
        alert(detailsText);
      }
    } catch (err) {
      logError('Failed to fetch user details', { error: err.message, stack: err.stack });
      alert('Failed to load user details');
    }
  };

  // Render routes using React Router
  const renderRoutes = () => (
    <Routes>
      {/* Home Route */}
      <Route path="/" element={<HomePage user={user} isLoggedIn={isLoggedIn} setCurrentPage={navigateToPage} />} />
      
      {/* Main Navigation Routes */}
      <Route path="/create" element={
        <ErrorBoundary title="Card Creation Error" message="Something went wrong while creating your card. Please try again.">
          <Suspense fallback={<LoadingSpinner message="Loading card creator..." />}>
            <CreateCardV2 
              user={user} 
              isLoggedIn={isLoggedIn} 
              setCurrentPage={navigateToPage}
            />
          </Suspense>
        </ErrorBoundary>
      } />
      
      {/* Same as /create - for navigation compatibility */}
      <Route path="/create-card" element={
        <ErrorBoundary title="Card Creation Error" message="Something went wrong while creating your card. Please try again.">
          <Suspense fallback={<LoadingSpinner message="Loading card creator..." />}>
            <CreateCardV2 
              user={user} 
              isLoggedIn={isLoggedIn} 
              setCurrentPage={navigateToPage}
            />
          </Suspense>
        </ErrorBoundary>
      } />
      
      {/* V2 Card Creator - Old Design (Legacy) */}
      <Route path="/create-v2" element={
        <ErrorBoundary title="Card Creation Error" message="Something went wrong while creating your card. Please try again.">
          <Suspense fallback={<LoadingSpinner message="Loading card creator..." />}>
            <CreateCard 
              user={user} 
              isLoggedIn={isLoggedIn} 
              onGenerationStart={notifyGenerationStarted}
              setCurrentPage={navigateToPage}
            />
          </Suspense>
        </ErrorBoundary>
      } />
      
      {/* Processing Page - shown after card generation is queued */}
      <Route path="/processing" element={
        <Suspense fallback={<LoadingSpinner message="Loading..." />}>
          <ProcessingPage 
            onNavigate={navigateToPage}
            jobInfo={null}
          />
        </Suspense>
      } />
      
      <Route path="/pricing" element={
        <ErrorBoundary title="Pricing Error" message="Something went wrong while loading the pricing page. Please try again.">
          <Suspense fallback={<LoadingSpinner message="Loading pricing..." />}>
            <Pricing />
          </Suspense>
        </ErrorBoundary>
      } />
      
      <Route path="/dashboard" element={
        user && user.role === 'admin' ? (
          <ErrorBoundary title="Admin Dashboard Error" message="Unable to load the admin dashboard. Please refresh the page.">
            <Suspense fallback={<LoadingSpinner message="Loading admin dashboard..." />}>
              <AdminDashboard 
                user={user} 
                adminMetrics={adminMetrics}
                wsConnected={wsConnected}
                setCurrentPage={navigateToPage}
                handleViewUserDetails={handleViewUserDetails}
              />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <ErrorBoundary title="Dashboard Error" message="Unable to load your dashboard. Please refresh the page.">
            <Suspense fallback={<LoadingSpinner message="Loading dashboard..." />}>
              <Dashboard user={user} setCurrentPage={navigateToPage} />
            </Suspense>
          </ErrorBoundary>
        )
      } />
      
      <Route path="/my-creations" element={
        <ErrorBoundary title="My Creations Error" message="Unable to load your creations. Please try refreshing the page.">
          <Suspense fallback={<LoadingSpinner message="Loading your creations..." />}>
            <MyCards />
          </Suspense>
        </ErrorBoundary>
      } />
      
      {/* Legacy route for backward compatibility */}
      <Route path="/my-cards" element={
        <ErrorBoundary title="My Cards Error" message="Unable to load your cards. Please try refreshing the page.">
          <Suspense fallback={<LoadingSpinner message="Loading your cards..." />}>
            <MyCards />
          </Suspense>
        </ErrorBoundary>
      } />

      {/* User Area */}
      <Route path="/user" element={
        <ErrorBoundary title="User Area Error" message="Unable to load your account. Please try refreshing the page.">
          <Suspense fallback={<LoadingSpinner message="Loading your account..." />}>
            <UserArea />
          </Suspense>
        </ErrorBoundary>
      } />

      {/* Auth Routes */}
      <Route path="/login" element={
        <Suspense fallback={<LoadingSpinner message="Loading login..." />}>
          <Login onNavigate={navigateToPage} />
        </Suspense>
      } />
      
      <Route path="/register" element={
        <Suspense fallback={<LoadingSpinner message="Loading signup..." />}>
          <Register onNavigate={navigateToPage} />
        </Suspense>
      } />
      
      <Route path="/forgot-password" element={
        <Suspense fallback={<LoadingSpinner message="Loading forgot password..." />}>
          <ForgotPassword onNavigate={navigateToPage} />
        </Suspense>
      } />
      
      <Route path="/reset-password" element={
        <Suspense fallback={<LoadingSpinner message="Loading password reset..." />}>
          <ResetPassword onNavigate={navigateToPage} />
        </Suspense>
      } />
      
      <Route path="/verify-email" element={
        <Suspense fallback={<LoadingSpinner message="Loading email verification..." />}>
          <VerifyEmail 
            token={verificationToken} 
            onNavigate={navigateToPage}
          />
        </Suspense>
      } />
      
      <Route path="/payment-success" element={
        <ErrorBoundary title="Payment Success Error" message="Unable to load payment confirmation. Please check your dashboard for credit updates.">
          <Suspense fallback={<LoadingSpinner message="Loading payment confirmation..." />}>
            <PaymentSuccess 
              paymentData={JSON.parse(localStorage.getItem('paymentSuccess') || 'null')}
              onNavigate={navigateToPage}
            />
          </Suspense>
        </ErrorBoundary>
      } />
      
      {/* Policy Routes */}
      <Route path="/privacy-policy" element={
        <Suspense fallback={<LoadingSpinner message="Loading privacy policy..." />}>
          <PrivacyPolicy />
        </Suspense>
      } />
      
      <Route path="/terms-of-service" element={
        <Suspense fallback={<LoadingSpinner message="Loading terms of service..." />}>
          <TermsOfService />
        </Suspense>
      } />
      
      <Route path="/cookie-policy" element={
        <Suspense fallback={<LoadingSpinner message="Loading cookie policy..." />}>
          <CookiePolicy />
        </Suspense>
      } />
      
      {/* Test Routes */}
      <Route path="/pricing-test" element={
        <ErrorBoundary title="Pricing Test Error" message="Something went wrong while loading the pricing test. Please try again.">
          <Suspense fallback={<LoadingSpinner message="Loading pricing test..." />}>
            <PricingTest />
          </Suspense>
        </ErrorBoundary>
      } />

      {/* Achievements Route */}
      <Route path="/achievements" element={
        <ErrorBoundary title="Achievements Error" message="Unable to load achievements. Please try refreshing the page.">
          <Suspense fallback={<LoadingSpinner message="Loading achievements..." />}>
            <AchievementsPanel />
          </Suspense>
        </ErrorBoundary>
      } />

      {/* Credit History Route */}
      <Route path="/credit-history" element={
        <ErrorBoundary title="Credit History Error" message="Unable to load credit history. Please try refreshing the page.">
          <Suspense fallback={<LoadingSpinner message="Loading credit history..." />}>
            <CreditHistory />
          </Suspense>
        </ErrorBoundary>
      } />

      {/* Public Profile Route */}
      <Route path="/u/:username" element={
        <ErrorBoundary title="Profile Error" message="Unable to load profile. Please check the username and try again.">
          <Suspense fallback={<LoadingSpinner message="Loading profile..." />}>
            <PublicProfile />
          </Suspense>
        </ErrorBoundary>
      } />

      {/* Catch all route - redirect to home */}
      <Route path="*" element={<HomePage user={user} isLoggedIn={isLoggedIn} setCurrentPage={navigateToPage} />} />
    </Routes>
  );

  // Show loading screen while auth is initializing
  if (authLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: 'var(--color-background)', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '400px',
          width: '100%'
        }}>
          <h2 style={{ 
            fontSize: '2rem', 
            fontWeight: '600', 
            color: 'var(--color-text-primary)', 
            marginBottom: '1rem' 
          }}>
            Initializing CardMint
          </h2>
          <p style={{ 
            fontSize: '1.125rem', 
            color: 'var(--color-text-secondary)', 
            marginBottom: '2rem' 
          }}>
            Setting up your experience...
          </p>
          <div className="loading-bar-container" style={{ marginBottom: '1rem' }}>
            <div className="loading-bar"></div>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            opacity: 0.6
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-accent-blue)',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}></div>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-accent-blue)',
              animation: 'pulse 1.5s ease-in-out infinite 0.3s'
            }}></div>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-accent-blue)',
              animation: 'pulse 1.5s ease-in-out infinite 0.6s'
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  const isNative = Capacitor.isNativePlatform();
  const currentPath = location.pathname;
  
  // Get page title for native nav bar
  const getPageTitle = () => {
    if (currentPath === '/') return 'CardMint';
    if (currentPath === '/create' || currentPath === '/create-card') return 'Create Card';
    if (currentPath === '/my-creations' || currentPath === '/my-cards') return 'My Cards';
    if (currentPath === '/dashboard') return 'Dashboard';
    if (currentPath === '/pricing') return 'Pricing';
    return 'CardMint';
  };

  return (
    <div className="app">
      {/* Status Bar - Hide on native */}
      {!isNative && (
      <StatusBar 
        wsConnected={wsConnected}
        activeUsers={activeUsers}
        adminMetrics={adminMetrics}
      />
      )}

      {/* Native Navigation Bar */}
      {isNative && (
        <div className="native-nav-bar">
          {currentPath !== '/' && (
            <button 
              className="native-nav-bar-button"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          )}
          <div className="native-nav-bar-title">{getPageTitle()}</div>
          {isLoggedIn && (
            <button 
              className="native-nav-bar-button right"
              onClick={handleLogout}
            >
              Logout
            </button>
          )}
        </div>
      )}

      {/* Web Header - Hide on native */}
      {!isNative && (
      <Header 
        key={`header-${user?.email || 'anonymous'}-${isLoggedIn}`}
        user={user}
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
        darkMode={darkMode}
        toggleTheme={toggleTheme}
      />
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <NotificationCenter
          notifications={notifications}
          onRemove={removeNotification}
          onClear={clearNotifications}
        />
      )}

      {/* Main Content */}
      <main className={`main-content ${isNative ? 'native-scroll-view' : ''}`}>
        {isNative ? (
          <div className="native-page">
        {renderRoutes()}
          </div>
        ) : (
          renderRoutes()
        )}
      </main>

      {/* Native Tab Bar */}
      {isNative && isLoggedIn && (
        <div className="native-tab-bar">
          <a 
            href="/" 
            className={`native-tab-item ${currentPath === '/' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
          >
            <div className="native-tab-item-icon">🏠</div>
            <div className="native-tab-item-label">Home</div>
          </a>
          <a 
            href="/create-card" 
            className={`native-tab-item ${currentPath === '/create-card' || currentPath === '/create' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); navigate('/create-card'); }}
          >
            <div className="native-tab-item-icon">✨</div>
            <div className="native-tab-item-label">Create</div>
          </a>
          <a 
            href="/my-cards" 
            className={`native-tab-item ${currentPath === '/my-cards' || currentPath === '/my-creations' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); navigate('/my-cards'); }}
          >
            <div className="native-tab-item-icon">📚</div>
            <div className="native-tab-item-label">My Cards</div>
          </a>
          <a 
            href="/dashboard" 
            className={`native-tab-item ${currentPath === '/dashboard' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}
          >
            <div className="native-tab-item-icon">👤</div>
            <div className="native-tab-item-label">Profile</div>
          </a>
        </div>
      )}

      {/* Web Footer - Hide on native */}
      {!isNative && <Footer />}

      {/* Cookie Consent Banner */}
      {!isNative && <CookieConsent onNavigate={navigateToPage} />}
    </div>
  );
}

// Main App component with Router wrapper
function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App;
